import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { GeometricBackground } from '../components/GeometricBackground';
import { KhatimStarLogo } from '../components/AalimLogo';
import { BookOpen, Heart, Users, Shield, GraduationCap, Globe, ArrowRight } from 'lucide-react';

const fadeUp = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.5 } }),
};

const values = [
    { icon: BookOpen, title: "Quran & Sunnah First", desc: "Every answer is rooted in the Quran and the authentic Sunnah of the Prophet Muhammad (peace be upon him)." },
    { icon: Shield, title: "Scholarly Integrity", desc: "We rely on recognized scholars and verified sources — not opinions, not culture, not trends." },
    { icon: Users, title: "For All Muslims", desc: "Whether you're a new Muslim or a lifelong student of knowledge, aalim.ai meets you where you are." },
    { icon: Heart, title: "Built with Reverence", desc: "This is not just a tech product. It's a trust. We treat Islamic knowledge with the respect it deserves." },
    { icon: GraduationCap, title: "Learn, Don't Just Search", desc: "Go beyond surface-level answers. Understand the evidence, the context, and the wisdom behind every ruling." },
    { icon: Globe, title: "Accessible Anywhere", desc: "Islamic knowledge shouldn't be locked behind geography or language. We're building for the global Ummah." },
];

export default function AboutPage() {
    return (
        <div className="min-h-screen bg-background text-foreground">
            <GeometricBackground />

            {/* Header */}
            <header className="fixed top-0 w-full z-50 bg-background/80 backdrop-blur-md border-b border-border">
                <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-1">
                        <KhatimStarLogo size={36} />
                        <span className="font-heading text-xl font-semibold text-foreground -ml-1">aalim<span className="text-secondary">.ai</span></span>
                    </Link>
                    <div className="flex items-center gap-4">
                        <Link to="/blogs" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Blog</Link>
                        <Link to="/login">
                            <Button variant="outline" size="sm">Sign In</Button>
                        </Link>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="pt-32 pb-16 px-6">
                <div className="max-w-4xl mx-auto text-center">
                    <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={0}>
                        <span className="inline-block px-4 py-1.5 rounded-full bg-secondary/10 text-secondary text-sm font-medium mb-6 border border-secondary/20">
                            Our Mission
                        </span>
                    </motion.div>
                    <motion.h1 variants={fadeUp} custom={1} initial="hidden" animate="visible"
                        className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">
                        Authentic Islamic Knowledge,<br />
                        <span className="text-secondary">Powered by AI</span>
                    </motion.h1>
                    <motion.p variants={fadeUp} custom={2} initial="hidden" animate="visible"
                        className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                        aalim.ai was born from a simple need — Muslims deserve access to reliable, 
                        source-backed Islamic knowledge without confusion, without misguidance, and without barriers.
                    </motion.p>
                </div>
            </section>

            {/* Story */}
            <section className="py-16 px-6">
                <div className="max-w-3xl mx-auto">
                    <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
                        className="space-y-6 text-muted-foreground leading-relaxed">
                        <Card className="border-secondary/20 bg-secondary/5">
                            <CardContent className="p-8">
                                <p className="text-base leading-relaxed mb-4">
                                    Too many Muslims turn to Google, social media, or unverified sources for Islamic guidance — 
                                    and end up more confused than before. Weak hadiths get shared like facts. Cultural opinions get 
                                    mixed in with authentic rulings. And real scholarly knowledge stays buried in Arabic texts most 
                                    people can't access.
                                </p>
                                <p className="text-base leading-relaxed mb-4">
                                    <strong className="text-foreground">aalim.ai changes that.</strong> We built an AI-powered Islamic 
                                    knowledge assistant that only draws from the Quran, authenticated Hadith collections (Bukhari, Muslim, 
                                    Abu Dawud, Tirmidhi, Nasa'i, Ibn Majah), trusted fatwa sources, and classical scholarly works — including 
                                    full Arabic fiqh encyclopedias from all four major schools of thought.
                                </p>
                                <p className="text-base leading-relaxed mb-4">
                                    Whether you need a quick answer about wudu, a deep dive into inheritance law, or help with Quran 
                                    recitation and tajweed — aalim.ai is here for you. Our premium <strong className="text-foreground">Ustadh Mode</strong> even 
                                    lets you practice recitation with real-time AI feedback, like having a personal Quran teacher available 24/7.
                                </p>
                                <p className="text-base leading-relaxed">
                                    This isn't just another chatbot. It's a tool built with reverence for the Deen, 
                                    drawing from the Quran, Hadith, Tafsir, and scholarly fiqh works across Hanbali, Hanafi, Maliki, and Shafi'i traditions.
                                </p>
                            </CardContent>
                        </Card>
                    </motion.div>
                </div>
            </section>

            {/* Values */}
            <section className="py-16 px-6 bg-muted/30">
                <div className="max-w-6xl mx-auto">
                    <h2 className="font-heading text-2xl sm:text-3xl font-bold text-center text-foreground mb-12">
                        What We Stand For
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {values.map((v, i) => (
                            <motion.div key={i} variants={fadeUp} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true }}>
                                <Card className="h-full border-border hover:border-secondary/30 transition-colors">
                                    <CardContent className="p-6">
                                        <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center mb-4">
                                            <v.icon className="w-5 h-5 text-secondary" />
                                        </div>
                                        <h3 className="font-heading font-semibold text-foreground mb-2">{v.title}</h3>
                                        <p className="text-sm text-muted-foreground leading-relaxed">{v.desc}</p>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Knowledge Base Stats */}
            <section className="py-16 px-6">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground mb-4">
                        Our Knowledge Base
                    </h2>
                    <p className="text-muted-foreground mb-10 max-w-xl mx-auto">
                        Every answer is powered by a comprehensive library of authentic Islamic sources.
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                        {[
                            { num: "6", label: "Hadith Collections" },
                            { num: "4", label: "Fiqh Schools" },
                            { num: "70+", label: "Scholarly Volumes" },
                            { num: "10K+", label: "Fatwas & Rulings" },
                        ].map((stat, i) => (
                            <motion.div key={i} variants={fadeUp} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true }}>
                                <div className="text-center">
                                    <div className="text-3xl font-heading font-bold text-secondary mb-1">{stat.num}</div>
                                    <div className="text-sm text-muted-foreground">{stat.label}</div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-16 px-6 bg-secondary/5">
                <div className="max-w-2xl mx-auto text-center">
                    <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground mb-4">
                        Ready to Learn?
                    </h2>
                    <p className="text-muted-foreground mb-8">
                        Start asking questions grounded in authentic Islamic scholarship.
                    </p>
                    <Link to="/register">
                        <Button size="lg" className="gap-2 bg-secondary hover:bg-secondary/90 text-secondary-foreground">
                            Get Started Free <ArrowRight className="w-4 h-4" />
                        </Button>
                    </Link>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-12 border-t border-secondary/20">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
                        <div>
                            <div className="flex items-center gap-1 mb-3">
                                <KhatimStarLogo size={36} />
                                <span className="font-heading text-lg font-semibold -ml-1">aalim<span className="text-secondary">.ai</span></span>
                            </div>
                            <p className="text-sm text-muted-foreground">
                                Authentic Islamic knowledge, powered by AI.
                            </p>
                        </div>
                        <div>
                            <h4 className="font-semibold text-foreground mb-3 text-sm">Product</h4>
                            <div className="space-y-2">
                                <Link to="/chat" className="block text-sm text-muted-foreground hover:text-foreground transition-colors">Free Chat</Link>
                                <Link to="/subscription" className="block text-sm text-muted-foreground hover:text-foreground transition-colors">Ustadh Mode</Link>
                            </div>
                        </div>
                        <div>
                            <h4 className="font-semibold text-foreground mb-3 text-sm">Company</h4>
                            <div className="space-y-2">
                                <Link to="/about" className="block text-sm text-muted-foreground hover:text-foreground transition-colors">About</Link>
                                <Link to="/blogs" className="block text-sm text-muted-foreground hover:text-foreground transition-colors">Blog</Link>
                                <Link to="/terms" className="block text-sm text-muted-foreground hover:text-foreground transition-colors">Terms & Privacy</Link>
                            </div>
                        </div>
                        <div>
                            <h4 className="font-semibold text-foreground mb-3 text-sm">Contact</h4>
                            <div className="space-y-2">
                                <a href="mailto:contact@aalim.ai" className="block text-sm text-muted-foreground hover:text-foreground transition-colors">contact@aalim.ai</a>
                                <a href="mailto:business@aalim.ai" className="block text-sm text-muted-foreground hover:text-foreground transition-colors">business@aalim.ai</a>
                            </div>
                        </div>
                    </div>
                    <div className="border-t border-border pt-6 text-center text-sm text-muted-foreground">
                        &copy; 2026 aalim.ai &mdash; All answers sourced from Quran and authentic Sunnah.
                    </div>
                </div>
            </footer>
        </div>
    );
}
